// src/utils/nftCacheUtils.js

/**
 * NFT Cache System
 * Caches NFTs by wallet address and chain to avoid refetching on chain switches
 */

const NFT_CACHE_KEY = 'nftCache';
const CACHE_VERSION = '1.0';
const CACHE_EXPIRY_TIME = 10 * 60 * 1000; // 10 minutes

/**
 * Generate cache key for wallet address and chain
 * @param {string} walletAddress - Wallet address
 * @param {string} chainKey - Chain identifier
 * @returns {string} - Cache key
 */
const generateCacheKey = (walletAddress, chainKey) => {
  if (!walletAddress || !chainKey) return null;
  return `${walletAddress.toLowerCase()}-${chainKey}`;
};

/**
 * Get the full NFT cache from localStorage
 * @returns {Object} - Cache object
 */
const getFullCache = () => {
  try {
    const cacheData = localStorage.getItem(NFT_CACHE_KEY);
    if (!cacheData) return { version: CACHE_VERSION, data: {} };
    
    const parsed = JSON.parse(cacheData);
    
    // Check cache version and clear if outdated
    if (parsed.version !== CACHE_VERSION) {
      console.log('NFT cache version mismatch, clearing cache');
      return { version: CACHE_VERSION, data: {} };
    }
    
    return parsed;
  } catch (error) {
    console.error('Error reading NFT cache:', error);
    return { version: CACHE_VERSION, data: {} };
  }
};

/**
 * Save the full NFT cache to localStorage
 * @param {Object} cache - Cache object to save
 */
const saveFullCache = (cache) => {
  try {
    localStorage.setItem(NFT_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error('Error saving NFT cache:', error);
    // If localStorage is full, clear old entries and try again
    try {
      clearExpiredEntries();
      localStorage.setItem(NFT_CACHE_KEY, JSON.stringify(cache));
    } catch (retryError) {
      console.error('Failed to save NFT cache after cleanup:', retryError);
    }
  }
};

/**
 * Check if a cache entry is expired
 * @param {Object} entry - Cache entry
 * @returns {boolean} - Whether the entry is expired
 */
const isExpired = (entry) => {
  if (!entry || !entry.timestamp) return true;
  return Date.now() - entry.timestamp > CACHE_EXPIRY_TIME;
};

/**
 * Clear expired entries from cache
 */
const clearExpiredEntries = () => {
  try {
    const cache = getFullCache();
    const now = Date.now();
    
    Object.keys(cache.data).forEach(key => {
      if (isExpired(cache.data[key])) {
        delete cache.data[key];
      }
    });
    
    saveFullCache(cache);
    console.log('Cleared expired NFT cache entries');
  } catch (error) {
    console.error('Error clearing expired NFT cache entries:', error);
  }
};

/**
 * Get cached NFTs for a wallet and chain
 * @param {string} walletAddress - Wallet address
 * @param {string} chainKey - Chain identifier
 * @returns {Array|null} - Cached NFTs or null if not found/expired
 */
export const getCachedNFTs = (walletAddress, chainKey) => {
  try {
    const cacheKey = generateCacheKey(walletAddress, chainKey);
    if (!cacheKey) return null;
    
    const cache = getFullCache();
    const entry = cache.data[cacheKey];
    
    if (!entry) {
      console.log(`No NFT cache found for ${chainKey}:${walletAddress}`);
      return null;
    }
    
    if (isExpired(entry)) {
      console.log(`NFT cache expired for ${chainKey}:${walletAddress}`);
      return null;
    }
    
    console.log(`Retrieved ${entry.nfts.length} NFTs from cache for ${chainKey}:${walletAddress}`);
    return entry.nfts;
  } catch (error) {
    console.error('Error getting cached NFTs:', error);
    return null;
  }
};

/**
 * Cache NFTs for a wallet and chain
 * @param {string} walletAddress - Wallet address
 * @param {string} chainKey - Chain identifier
 * @param {Array} nfts - NFTs to cache
 */
export const cacheNFTs = (walletAddress, chainKey, nfts) => {
  try {
    const cacheKey = generateCacheKey(walletAddress, chainKey);
    if (!cacheKey) return;
    
    const cache = getFullCache();
    
    // Create cache entry
    cache.data[cacheKey] = {
      nfts: nfts || [],
      timestamp: Date.now(),
      walletAddress: walletAddress.toLowerCase(),
      chainKey: chainKey,
      count: (nfts || []).length
    };
    
    saveFullCache(cache);
    console.log(`Cached ${(nfts || []).length} NFTs for ${chainKey}:${walletAddress}`);
  } catch (error) {
    console.error('Error caching NFTs:', error);
  }
};

/**
 * Clear cache for a specific wallet and chain
 * @param {string} walletAddress - Wallet address
 * @param {string} chainKey - Chain identifier
 */
export const clearCachedNFTs = (walletAddress, chainKey) => {
  try {
    const cacheKey = generateCacheKey(walletAddress, chainKey);
    if (!cacheKey) return;
    
    const cache = getFullCache();
    if (cache.data[cacheKey]) {
      delete cache.data[cacheKey];
      saveFullCache(cache);
      console.log(`Cleared NFT cache for ${chainKey}:${walletAddress}`);
    }
  } catch (error) {
    console.error('Error clearing cached NFTs:', error);
  }
};

/**
 * Clear cache for a specific wallet across all chains
 * @param {string} walletAddress - Wallet address
 */
export const clearWalletNFTCache = (walletAddress) => {
  try {
    if (!walletAddress) return;
    
    const cache = getFullCache();
    const walletPrefix = walletAddress.toLowerCase();
    
    let clearedCount = 0;
    Object.keys(cache.data).forEach(key => {
      if (key.startsWith(walletPrefix + '-')) {
        delete cache.data[key];
        clearedCount++;
      }
    });
    
    if (clearedCount > 0) {
      saveFullCache(cache);
      console.log(`Cleared NFT cache for wallet ${walletAddress} (${clearedCount} entries)`);
    }
  } catch (error) {
    console.error('Error clearing wallet NFT cache:', error);
  }
};

/**
 * Clear all NFT cache
 */
export const clearAllNFTCache = () => {
  try {
    localStorage.removeItem(NFT_CACHE_KEY);
    console.log('Cleared all NFT cache');
  } catch (error) {
    console.error('Error clearing all NFT cache:', error);
  }
};

/**
 * Get cache statistics
 * @returns {Object} - Cache statistics
 */
export const getNFTCacheStats = () => {
  try {
    const cache = getFullCache();
    const entries = Object.keys(cache.data);
    const now = Date.now();
    
    let totalNFTs = 0;
    let expiredEntries = 0;
    const chainStats = {};
    
    entries.forEach(key => {
      const entry = cache.data[key];
      if (isExpired(entry)) {
        expiredEntries++;
      } else {
        totalNFTs += entry.count || 0;
        
        if (!chainStats[entry.chainKey]) {
          chainStats[entry.chainKey] = { wallets: 0, nfts: 0 };
        }
        chainStats[entry.chainKey].wallets++;
        chainStats[entry.chainKey].nfts += entry.count || 0;
      }
    });
    
    return {
      totalEntries: entries.length,
      expiredEntries,
      activeEntries: entries.length - expiredEntries,
      totalNFTs,
      chainStats,
      cacheSize: JSON.stringify(cache).length
    };
  } catch (error) {
    console.error('Error getting NFT cache stats:', error);
    return {
      totalEntries: 0,
      expiredEntries: 0,
      activeEntries: 0,
      totalNFTs: 0,
      chainStats: {},
      cacheSize: 0
    };
  }
};

/**
 * Check if we have cached NFTs for a wallet and chain
 * @param {string} walletAddress - Wallet address
 * @param {string} chainKey - Chain identifier
 * @returns {boolean} - Whether we have valid cached NFTs
 */
export const hasCachedNFTs = (walletAddress, chainKey) => {
  const cached = getCachedNFTs(walletAddress, chainKey);
  return cached !== null;
};

/**
 * Get cache info for debugging
 * @param {string} walletAddress - Wallet address
 * @param {string} chainKey - Chain identifier
 * @returns {Object|null} - Cache info or null
 */
export const getNFTCacheInfo = (walletAddress, chainKey) => {
  try {
    const cacheKey = generateCacheKey(walletAddress, chainKey);
    if (!cacheKey) return null;
    
    const cache = getFullCache();
    const entry = cache.data[cacheKey];
    
    if (!entry) return null;
    
    return {
      cacheKey,
      timestamp: entry.timestamp,
      age: Date.now() - entry.timestamp,
      isExpired: isExpired(entry),
      nftCount: entry.count || 0,
      walletAddress: entry.walletAddress,
      chainKey: entry.chainKey
    };
  } catch (error) {
    console.error('Error getting NFT cache info:', error);
    return null;
  }
};

// Automatically clear expired entries on module load
clearExpiredEntries();